import React, { useEffect, useState } from 'react';

import { format } from 'date-fns';

import { decimalUpTo } from 'Helpers/currencyFormat';
import { formatMoney } from 'Helpers/formatService';

// Headline diff keys that are money figures (formatted as currency); the rest
// (departments, cost_centres, workforce) are integer counts.
const MONEY_KEYS = ['revenue', 'expenditure', 'net', 'committed_revenue', 'committed_expenditure'];

function formatChangeValue(key, value) {
    if (MONEY_KEYS.includes(key)) {
        return formatMoney(Number(value) || 0);
    }

    return decimalUpTo(Number(value) || 0);
}

function safeDate(value) {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);

    return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * IB-758 — Budget Versions v2, Phase D.
 *
 * Right-hand slide-in drawer showing a version's full audit trail: the "Version
 * created" entry plus one "Snapshot edited" entry per edit session, oldest to
 * newest. Each edit shows when, by whom, the reason (Why), and a headline-only
 * "What changed" list (label + from → to). Fetches budgets.version-history on
 * open. Closes on ✕, scrim click, or Escape.
 */
export default function VersionHistoryDrawer({ budget, historyUrl, onClose }) {
    const [timeline, setTimeline] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        axios
            .get(historyUrl.replace('budget_id', budget.id))
            .then((res) => {
                const data = res.data && res.data.timeline ? res.data.timeline : res.data;
                setTimeline(Array.isArray(data) ? data : []);
                setIsLoading(false);
            })
            .catch(() => {
                setHasError(true);
                setIsLoading(false);
            });

        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const created = timeline ? timeline.find((entry) => entry.type === 'created') : null;
    const editsSince = timeline ? timeline.filter((entry) => entry.type === 'edit').length : 0;
    const createdDate = created ? safeDate(created.when) : null;

    return (
        <>
            <div className="tw-fixed tw-inset-0 tw-z-90 tw-bg-black/40" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="version-history-title"
                className="tw-fixed tw-inset-y-0 tw-end-0 tw-z-100 tw-flex tw-h-full tw-w-full tw-max-w-md tw-flex-col tw-bg-white tw-shadow-primary"
            >
                <div className="tw-relative tw-border-b tw-border-grey tw-px-6 tw-py-5">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={$t('Close')}
                        className="tw-absolute tw-end-4 tw-top-4 tw-text-xl tw-leading-none tw-text-charcoal-50 hover:tw-text-charcoal"
                    >
                        ✕
                    </button>
                    <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{$t('Version history')}</div>
                    <div id="version-history-title" className="tw-mt-1 tw-text-lg tw-font-bold tw-text-charcoal">
                        {budget.name} {$t('(v{{version}})', { version: budget.version })}
                    </div>
                    <div className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                        {created
                            ? $t('Created {{date}} by {{who}} · {{count}} edit(s) since', {
                                  date: createdDate ? format(createdDate, 'dd MMM yyyy') : '—',
                                  who: created.who || $t('Unknown'),
                                  count: editsSince,
                              })
                            : $t('Full audit trail · who changed what, when, and why')}
                    </div>
                </div>

                <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-4">
                    {isLoading && <div className="tw-py-8 tw-text-center tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</div>}
                    {hasError && <div className="tw-py-8 tw-text-center tw-text-sm tw-text-red">{$t('Could not load the version history.')}</div>}
                    {!isLoading && !hasError && (
                        <ol className="tw-relative tw-ms-2 tw-border-s-2 tw-border-grey tw-ps-6">
                            {timeline.map((entry, index) => {
                                const isEdit = entry.type === 'edit';
                                const when = safeDate(entry.when);

                                return (
                                    <li key={index} className="tw-relative tw-pb-6 last:tw-pb-0">
                                        <span
                                            className={
                                                'tw-absolute tw-h-3.5 tw-w-3.5 tw-rounded-full tw-border-2 tw-border-white ' +
                                                (isEdit ? 'tw-bg-amber-500' : 'tw-bg-blue')
                                            }
                                            style={{ insetInlineStart: '-1.9rem', top: '0.25rem' }}
                                            aria-hidden="true"
                                        />
                                        <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                                            {when ? format(when, 'dd MMM yyyy, HH:mm') : '—'}
                                        </div>
                                        <div className="tw-mt-1 tw-flex tw-items-center tw-gap-x-2">
                                            <span className="tw-text-sm tw-font-semibold tw-text-charcoal">
                                                {isEdit ? $t('Snapshot edited') : $t('Version created')}
                                            </span>
                                            <span
                                                className={
                                                    'tw-rounded tw-px-2 tw-py-0.5 tw-text-2xs tw-font-semibold tw-uppercase ' +
                                                    (isEdit ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-blue-10 tw-text-blue')
                                                }
                                            >
                                                {isEdit ? $t('Edit') : $t('Created')}
                                            </span>
                                        </div>
                                        {entry.who && (
                                            <div className="tw-mt-1 tw-text-xs tw-text-charcoal-75">
                                                {$t('by')} <span className="tw-font-semibold">{entry.who}</span>
                                            </div>
                                        )}
                                        {entry.why && (
                                            <div className="tw-mt-2 tw-rounded-e-md tw-border-s-2 tw-border-charcoal-25 tw-bg-background tw-px-3 tw-py-2">
                                                <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                                                    {$t('Why')}
                                                </div>
                                                <div className="tw-mt-0.5 tw-text-xs tw-text-charcoal">{entry.why}</div>
                                            </div>
                                        )}
                                        {entry.changes && entry.changes.length > 0 && (
                                            <div className="tw-mt-2">
                                                <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                                                    {$t('What changed')}
                                                </div>
                                                <ul className="tw-mt-1 tw-flex tw-flex-col tw-gap-y-1">
                                                    {entry.changes.map((change, changeIndex) => (
                                                        <li key={changeIndex} className="tw-flex tw-items-start tw-gap-x-2 tw-text-xs tw-text-charcoal">
                                                            <span className="tw-font-bold tw-text-amber-700" aria-hidden="true">
                                                                →
                                                            </span>
                                                            <span>
                                                                <span className="tw-text-charcoal-75">{change.label}: </span>
                                                                <span className="tw-font-semibold">{formatChangeValue(change.key, change.from)}</span>
                                                                <span className="tw-text-charcoal-50"> → </span>
                                                                <span className="tw-font-semibold">{formatChangeValue(change.key, change.to)}</span>
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </div>
        </>
    );
}
